import { useMemo, useCallback } from "react"
import { filter } from "lodash"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createFactorSensitivityMultiDateQuery } from "gatherers/multi-date/factor-sensitivity"
import { filterItemsWithData, extractDatesFromFirstSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { ChartColors } from "connected-components/graph/types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { graphFactorSensitivityTemplateKey, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { FactorSensitivityModelMetadata as ModelMetadata, type SensitivityType } from "./types"
import { DEFAULT_THRESHOLD_PARAMS } from "helpers/threshold"
import { useGraphData } from "../../hooks/use-graph-data"

interface FactorSensitivityGraphProps {
  modelMetadata: ModelMetadata
  sensitivityType: SensitivityType
}

interface FactorSensitivityItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: string
}

function createFactorSensitivityItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
): FactorSensitivityItem[] {
  if (!modelMetadata?.factors) return []

  const factors = selectedCategory === "total"
    ? modelMetadata.factors
    : filter(modelMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: factor.label,
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor",
  }))
}

export default function FactorSensitivityGraph({ modelMetadata, sensitivityType }: FactorSensitivityGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(graphFactorSensitivityTemplateKey(sensitivityType), GRAPH_CATEGORY_DEFAULTS)
  const [{ isIntradayMode }] = useIntradayState()

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const drilldownView = sensitivityType === "historical" ? "historicalFactorSensitivity" : "predictedFactorSensitivity"

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined) => {
    if (!panelId || !focusedItemId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.factor-sensitivity",
      title: "Asset View: Factor Sensitivity",
      view: drilldownView,
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: clickPosition?.date ?? endDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: focusedItemId,
        selectedCategory,
        clickedDate: clickPosition?.date,
      },
    }))
  }, [panelId, openDrilldown, drilldownView, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter, normalization)
  const segment = useFilteredSegment(portfolioFilter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(
    () => createFactorSensitivityItems(modelMetadata, selectedCategory, disabledItems),
    [modelMetadata, selectedCategory, disabledItems],
  )

  const filterItems = useCallback(
    (items: FactorSensitivityItem[], data: unknown[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => [
    // TODO: [INTRADAY-BATCH] replace with intraday batch data when isIntradayEnabled
    createFactorSensitivityMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      sensitivityType,
    ),
  ], [modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, sensitivityType])

  return <DataIntegrator
    dataTransform={extractDatesFromFirstSeries}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      thresholdParams: DEFAULT_THRESHOLD_PARAMS,
      onDotClick: handleDotClick,
      loadingMessage: "Loading factor sensitivity data...",
      errorMessage: "Failed to load factor sensitivity data. Please try again.",
      emptyMessage: "No factor sensitivity data available for the selected period.",
    }}
  />
}
